import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCommentDots,
  FaDownload,
  FaEnvelope,
  FaEye,
  FaIndustry,
  FaPrint,
  FaRedo,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/buyer_contract_manufacturer_agreement.scss";
import "../../assets/styles/seller_sign_agreement.scss";

function getObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function unwrapApiEnvelope(raw) {
  if (
    raw != null &&
    typeof raw === "object" &&
    raw.data != null &&
    typeof raw.data === "object" &&
    !Array.isArray(raw.data)
  ) {
    return raw.data;
  }
  return raw;
}

function dash(v) {
  const s = v == null ? "" : String(v).trim();
  return s || "—";
}

/** Normalize purchase agreement row status from API (handles alternate keys / casing). */
function normalizeAgreementStatus(agreement) {
  if (!agreement) return "";
  const raw =
    agreement.status ?? agreement.agreement_status ?? agreement.agreementStatus ?? agreement.Status;
  return String(raw ?? "")
    .trim()
    .toLowerCase();
}

function toNumber(v) {
  if (v == null || v === "") return NaN;
  return Number(String(v).replace(/,/g, ""));
}

function formatCurrency(amount, currency = "USD") {
  const n = toNumber(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || "USD"} ${n}`;
  }
}

function resolveOptionLabel(rawValue, options = []) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  const byValue = options.find((opt) => String(opt?.value ?? "").trim() === raw);
  if (byValue?.label) return String(byValue.label).trim();
  const byLabel = options.find(
    (opt) => String(opt?.label ?? "").trim().toLowerCase() === raw.toLowerCase(),
  );
  if (byLabel?.label) return String(byLabel.label).trim();
  return raw;
}

function formatDateShort(iso) {
  if (!iso || !String(iso).trim()) return "—";
  const raw = String(iso).trim().slice(0, 10);
  const d = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(d);
}

function daysBetween(start, end) {
  if (!start || !end) return null;
  const a = new Date(`${String(start).trim().slice(0, 10)}T12:00:00`);
  const b = new Date(`${String(end).trim().slice(0, 10)}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
}

const SIGN_NAME_MAX = 200;
const SIGN_ROLE_MAX = 120;
/** Minimum length for base64 image data URL (rejects empty/trivial payloads). */
const SIGNATURE_DATA_MIN_LEN = 80;
const UPLOAD_MAX_BYTES = 2.5 * 1024 * 1024;

/**
 * @returns {{ ok: boolean, errors: Record<string, string>, signatureData: string | null, fullName: string, designation: string }}
 */
function validateSellerSignForm({
  fullName,
  designation,
  sigMethod,
  canvasDirty,
  canvasRef,
  typeText,
  uploadDataUrl,
}) {
  const errors = {};
  const fn = String(fullName ?? "").trim();
  const des = String(designation ?? "").trim();

  if (!fn) errors.fullName = "Full name is required.";
  else if (fn.length > SIGN_NAME_MAX) errors.fullName = `Use at most ${SIGN_NAME_MAX} characters.`;

  if (!des) errors.designation = "Designation is required.";
  else if (des.length > SIGN_ROLE_MAX) errors.designation = `Use at most ${SIGN_ROLE_MAX} characters.`;

  let signatureData = "";
  if (sigMethod === "draw") {
    const canvas = canvasRef?.current;
    if (!canvas || !canvasDirty) {
      errors.signature = "Draw your signature on the canvas.";
    } else {
      signatureData = canvas.toDataURL("image/png");
    }
  } else if (sigMethod === "type") {
    const t = String(typeText ?? "").trim();
    if (!t) errors.signature = "Type your name as your signature.";
    else signatureData = textSignatureToDataUrl(t);
  } else if (sigMethod === "upload") {
    const u = String(uploadDataUrl ?? "").trim();
    if (!u) errors.signature = "Choose an image file for your signature.";
    else if (!/^data:image\//i.test(u)) errors.signature = "File must be an image (PNG, JPG, GIF, or WebP).";
    else signatureData = u;
  } else {
    errors.signature = "Select Draw, Type, or Upload.";
  }

  if (!errors.signature && signatureData.length < SIGNATURE_DATA_MIN_LEN) {
    errors.signature = "Signature data is invalid or incomplete. Try again.";
  }

  const ok = Object.keys(errors).length === 0;
  return {
    ok,
    errors,
    signatureData: ok ? signatureData : null,
    fullName: fn,
    designation: des,
  };
}

function mapApiFieldErrorsToSignState(fieldErrors) {
  if (!fieldErrors || typeof fieldErrors !== "object") return {};
  const out = {};
  if (fieldErrors.fullName) out.fullName = String(fieldErrors.fullName);
  if (fieldErrors.designation) out.designation = String(fieldErrors.designation);
  if (fieldErrors.signatureData) out.signature = String(fieldErrors.signatureData);
  if (fieldErrors.signatureMethod) out.signature = out.signature || String(fieldErrors.signatureMethod);
  return out;
}

function textSignatureToDataUrl(text) {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 200;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#111827";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = 'italic 42px "Segoe Script", "Brush Script MT", "Apple Chancery", cursive';
  const t = String(text || "").trim() || "Signature";
  ctx.fillText(t, canvas.width / 2, canvas.height / 2, canvas.width - 40);
  return canvas.toDataURL("image/png");
}

function getCanvasPos(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const clientX = e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

const AGREEMENT_TYPE_OPTIONS = [
  { value: "purchase_agreement", label: "Purchase Agreement" },
  { value: "service_agreement", label: "Service Agreement" },
  { value: "non_disclosure_agreement", label: "Non-Disclosure Agreement" },
  { value: "master_service_agreement", label: "Master Service Agreement" },
];

const GOVERNING_LAW_OPTIONS = [
  { value: "in", label: "Indian Law" },
  { value: "us", label: "US Law" },
  { value: "uk", label: "UK Law" },
  { value: "sg", label: "Singapore Law" },
];

const IP_OWNERSHIP_OPTIONS = [
  { value: "buyer_retains_all", label: "Buyer retains all IP rights" },
  { value: "manufacturer_retains_all", label: "Manufacturer retains all IP rights" },
  { value: "shared_ownership", label: "Shared ownership" },
  { value: "custom_arrangement", label: "Custom arrangement" },
];

const CONTRACTING_PARTY_BLOCKS = [
  { key: "buyer", heading: "Buyer (First Party)" },
  { key: "manufacturer", heading: "Manufacturer (Second Party)" },
];

const TERMINATION_NOTICE_OPTIONS = [
  { value: "15", label: "15 Days" },
  { value: "30", label: "30 Days" },
  { value: "60", label: "60 Days" },
  { value: "90", label: "90 Days" },
];

const DISPUTE_RESOLUTION_OPTIONS = [
  { value: "arbitration", label: "Arbitration" },
  { value: "mediation", label: "Mediation" },
  { value: "litigation", label: "Litigation" },
];

function AgreementPreviewBody({
  form,
  summary,
  currencySelectOptions,
  paymentTermsSelectOptions,
  incotermsSelectOptions,
}) {
  return (
    <div className="cm-agreement-preview-content cm-agreement-seller-view" id="cm-agreement-seller-print">
      <header className="cm-agreement-preview-doc-head">
        <h1 className="cm-agreement-preview-doc-title">{dash(form.agreementInfo.title)}</h1>
        <p className="cm-agreement-preview-doc-meta">
          {dash(form.agreementInfo.agreementNumber)} · RFQ {summary.rfqNo} · Quotation {summary.qtNo}
        </p>
      </header>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Agreement basic information</h3>
        <dl className="cm-agreement-preview-dl">
          <div>
            <dt>Agreement type</dt>
            <dd>{resolveOptionLabel(form.agreementInfo.agreementType, AGREEMENT_TYPE_OPTIONS)}</dd>
          </div>
          <div>
            <dt>Governing law</dt>
            <dd>{resolveOptionLabel(form.agreementInfo.governingLaw, GOVERNING_LAW_OPTIONS)}</dd>
          </div>
          <div>
            <dt>Start date</dt>
            <dd>{dash(form.agreementInfo.effectiveDate)}</dd>
          </div>
          <div>
            <dt>End date</dt>
            <dd>{dash(form.agreementInfo.validUntil)}</dd>
          </div>
        </dl>
      </section>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Contracting parties</h3>
        {CONTRACTING_PARTY_BLOCKS.map(({ key, heading }) => (
          <div key={key} className="cm-agreement-preview-party">
            <h4 className="cm-agreement-preview-h4">{heading}</h4>
            <dl className="cm-agreement-preview-dl cm-agreement-preview-dl--dense">
              <div>
                <dt>Company</dt>
                <dd>{dash(form.parties[key]?.companyName)}</dd>
              </div>
              <div>
                <dt>Authorized representative</dt>
                <dd>{dash(form.parties[key]?.authorizedRepresentative)}</dd>
              </div>
              <div>
                <dt>Designation</dt>
                <dd>{dash(form.parties[key]?.designation)}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{dash(form.parties[key]?.email)}</dd>
              </div>
              <div className="cm-agreement-preview-span-2">
                <dt>Registered address</dt>
                <dd>{dash(form.parties[key]?.registeredAddress)}</dd>
              </div>
              <div>
                <dt>Contact</dt>
                <dd>{dash(form.parties[key]?.contactNumber)}</dd>
              </div>
            </dl>
          </div>
        ))}
      </section>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Commercial terms</h3>
        <dl className="cm-agreement-preview-dl">
          <div>
            <dt>Total contract value</dt>
            <dd>
              {form.pricing?.totalAmount !== "" && form.pricing?.totalAmount != null
                ? formatCurrency(form.pricing.totalAmount, String(form.agreementInfo.currency || "USD"))
                : "—"}
            </dd>
          </div>
          <div>
            <dt>Currency</dt>
            <dd>
              {resolveOptionLabel(form.agreementInfo.currency, currencySelectOptions) ||
                dash(form.agreementInfo.currency)}
            </dd>
          </div>
          <div>
            <dt>Payment terms</dt>
            <dd>{resolveOptionLabel(form.commercial.paymentTerms, paymentTermsSelectOptions)}</dd>
          </div>
          <div>
            <dt>Incoterms</dt>
            <dd>{resolveOptionLabel(form.commercial.incoterms, incotermsSelectOptions)}</dd>
          </div>
          <div>
            <dt>Late delivery penalty</dt>
            <dd>{dash(form.commercial.lateDeliveryPenalty)}</dd>
          </div>
          <div>
            <dt>Warranty period</dt>
            <dd>{dash(form.commercial.warrantyPeriod)}</dd>
          </div>
        </dl>
      </section>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Payment milestones</h3>
        {(form.paymentMilestones || []).length === 0 ? (
          <p className="cm-agreement-preview-muted">No milestones.</p>
        ) : (
          <table className="cm-agreement-preview-table">
            <thead>
              <tr>
                <th>Milestone</th>
                <th>Deliverable</th>
                <th>Due date</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {(form.paymentMilestones || []).map((m) => (
                <tr key={m.id || m.name}>
                  <td>{dash(m.name)}</td>
                  <td>{dash(m.deliverable)}</td>
                  <td>{dash(m.dueDate)}</td>
                  <td>{dash(m.paymentPercent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Agreement clauses</h3>
        <div className="cm-agreement-preview-block">
          <strong>1. Scope of work</strong>
          <p>{dash(form.agreementClauses?.scopeOfWork)}</p>
        </div>
        <div className="cm-agreement-preview-block">
          <strong>2. Quality standards</strong>
          <p>{dash(form.agreementClauses?.qualityStandards)}</p>
        </div>
        <div className="cm-agreement-preview-block">
          <strong>3. Delivery terms</strong>
          <p>{dash(form.agreementClauses?.deliveryTerms)}</p>
        </div>
      </section>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Legal</h3>
        <dl className="cm-agreement-preview-dl">
          <div>
            <dt>Termination notice</dt>
            <dd>{resolveOptionLabel(form.legal?.terminationNoticePeriod, TERMINATION_NOTICE_OPTIONS)}</dd>
          </div>
          <div>
            <dt>Dispute resolution</dt>
            <dd>{resolveOptionLabel(form.legal?.disputeResolution, DISPUTE_RESOLUTION_OPTIONS)}</dd>
          </div>
        </dl>
      </section>

      <section className="cm-agreement-preview-section">
        <h3 className="cm-agreement-preview-h3">Confidentiality &amp; IP</h3>
        <dl className="cm-agreement-preview-dl">
          <div>
            <dt>IP ownership</dt>
            <dd>{resolveOptionLabel(form.confidentiality?.ipOwnership, IP_OWNERSHIP_OPTIONS)}</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

export default function SellerContractManufacturerAgreementPage() {
  const { rfqId: rfqIdParam } = useParams();
  const rfqId = Number(rfqIdParam);
  const navigate = useNavigate();
  const { currencyOptions } = useCurrencyMaster();
  const { shippingTermsOptions, paymentScheduleOptions } = useFactoryDetailsMaster();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [signing, setSigning] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [signerTab, setSignerTab] = useState("manufacturer");
  const [fullName, setFullName] = useState("");
  const [designation, setDesignation] = useState("");
  const [sigMethod, setSigMethod] = useState("draw");
  const [typeText, setTypeText] = useState("");
  const [uploadDataUrl, setUploadDataUrl] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [canvasDirty, setCanvasDirty] = useState(false);
  const canvasRef = useRef(null);
  const signerDefaultsPopulatedRef = useRef(null);
  /** Inline validation: fullName, designation, signature (maps API `signatureData`). */
  const [signFieldErrors, setSignFieldErrors] = useState({});

  const currencySelectOptions = useMemo(() => {
    if (currencyOptions?.length) {
      return currencyOptions
        .map((o) => ({
          value: String(o.currencyCode || o.currency_code || o.code || "").trim(),
          label: o.label || o.currencyCode || "",
        }))
        .filter((o) => o.value);
    }
    return [];
  }, [currencyOptions]);

  const paymentTermsSelectOptions = useMemo(() => {
    return paymentScheduleOptions?.length ? paymentScheduleOptions : [];
  }, [paymentScheduleOptions]);

  const incotermsSelectOptions = useMemo(() => {
    return shippingTermsOptions?.length ? shippingTermsOptions : [];
  }, [shippingTermsOptions]);

  const loadContext = useCallback(async () => {
    const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_AGREEMENT(rfqId));
    return unwrapApiEnvelope(res);
  }, [rfqId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!Number.isFinite(rfqId) || rfqId <= 0) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const payload = await loadContext();
        if (!cancelled) setCtx(payload);
      } catch (e) {
        const m = e?.response?.data?.message || e?.message || "Unable to load agreement";
        toast.error(m);
        if (!cancelled) setCtx(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rfqId, loadContext]);

  const agreement = ctx?.agreement;
  const st = normalizeAgreementStatus(agreement);
  const form = useMemo(() => {
    const payloadJson = getObj(agreement?.agreement_payload_json);
    return {
      agreementInfo: { ...getObj(payloadJson.agreementInfo) },
      parties: {
        buyer: { ...getObj(getObj(payloadJson.parties).buyer) },
        manufacturer: { ...getObj(getObj(payloadJson.parties).manufacturer) },
      },
      pricing: { ...getObj(payloadJson.pricing) },
      commercial: { ...getObj(payloadJson.commercial) },
      agreementClauses: { ...getObj(payloadJson.agreementClauses) },
      paymentMilestones: Array.isArray(payloadJson.paymentMilestones) ? payloadJson.paymentMilestones : [],
      legal: { ...getObj(payloadJson.legal) },
      confidentiality: { ...getObj(payloadJson.confidentiality) },
      customClauses: Array.isArray(payloadJson.customClauses) ? payloadJson.customClauses : [],
      signatures: { ...getObj(payloadJson.signatures) },
    };
  }, [agreement]);

  const summary = useMemo(() => {
    const rfq = ctx?.rfq || {};
    const quote = ctx?.quote || null;
    return {
      rfqNo: rfq?.id ? `RFQ-${String(rfq.id).padStart(5, "0")}` : "—",
      qtNo: quote?.id ? `QT-${String(quote.id).padStart(6, "0")}` : "—",
    };
  }, [ctx]);

  const signView = useMemo(() => {
    if (ctx?.signView) return ctx.signView;
    if (!agreement) return null;
    const p = getObj(agreement.agreement_payload_json);
    const ai = getObj(p.agreementInfo);
    const parties = getObj(p.parties);
    const buyerP = getObj(parties.buyer);
    const mfrP = getObj(parties.manufacturer);
    const pricing = getObj(p.pricing);
    const sigRoot = getObj(p.signatures);
    const buyerSig = getObj(sigRoot.buyer);
    const mfrSig = getObj(sigRoot.manufacturer);
    let done = 0;
    if (buyerSig.signedAt) done += 1;
    if (mfrSig.signedAt) done += 1;
    const cur = String(ai.currency || "USD");
    const startRaw = String(ai.effectiveDate || "");
    const endRaw = String(ai.validUntil || "");
    const agreementId =
      String(ai.agreementNumber || "").trim() ||
      `AGR-${new Date().getFullYear()}-${String(agreement.id).padStart(5, "0")}`;
    return {
      agreementId,
      title: String(ai.title || "Manufacturing Service Agreement"),
      contractValue: formatCurrency(pricing.totalAmount, cur),
      currency: cur,
      startDate: formatDateShort(startRaw),
      endDate: formatDateShort(endRaw),
      durationDays: daysBetween(startRaw, endRaw),
      buyerName: String(buyerP.companyName || ctx?.buyerCompany?.company_name || "").trim() || "—",
      manufacturerName: String(mfrP.companyName || ctx?.sellerCompany?.company_name || "").trim() || "—",
      buyerContactEmail: String(buyerP.email || "").trim() || null,
      signaturesDone: done,
      signaturesTotal: 2,
      rfqId,
      sellerCanSign: st === "submitted",
    };
  }, [ctx, agreement, st, rfqId]);

  const canSign = signerTab === "manufacturer" && (signView?.sellerCanSign ?? st === "submitted");
  const isSigned = st === "signed";
  const awaitingBuyerSubmit = st === "draft" || st === "";
  const isCancelled = st === "cancelled";
  /** Allow typing / drawing whenever the signature form is shown (save still requires `canSign`). */
  const canEditSignerFields = !isSigned && !isCancelled && !awaitingBuyerSubmit;

  useEffect(() => {
    signerDefaultsPopulatedRef.current = null;
  }, [rfqId]);

  useEffect(() => {
    if (!agreement || !ctx) return;
    const key = `${rfqId}:${agreement.id}`;
    if (signerDefaultsPopulatedRef.current === key) return;
    signerDefaultsPopulatedRef.current = key;

    const p = getObj(agreement.agreement_payload_json);
    const mfrObj = getObj(getObj(p.parties).manufacturer);
    const sc = ctx.sellerCompany || {};
    const cd = getObj(sc.contact_details);

    const name =
      String(mfrObj.authorizedRepresentative || "").trim() ||
      String(cd.contact_name || "").trim() ||
      "";
    const des =
      String(mfrObj.designation || "").trim() ||
      String(sc.designation || "").trim() ||
      "";

    setFullName(name);
    setDesignation(des);
  }, [rfqId, agreement, ctx]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = 800;
    const h = 240;
    // Setting width/height to the *same* value does not clear the bitmap in browsers.
    // Always paint a fresh white surface (and reset any in-progress stroke).
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx2 = canvas.getContext("2d");
    ctx2.setTransform(1, 0, 0, 1, 0, 0);
    ctx2.globalCompositeOperation = "source-over";
    ctx2.fillStyle = "#ffffff";
    ctx2.fillRect(0, 0, w, h);
    ctx2.strokeStyle = "#111827";
    ctx2.lineWidth = 2.2;
    ctx2.lineCap = "round";
    ctx2.lineJoin = "round";
    ctx2.beginPath();
    setDrawing(false);
    setCanvasDirty(false);
  }, []);

  useEffect(() => {
    if (sigMethod === "draw" && canEditSignerFields) {
      initCanvas();
    }
  }, [sigMethod, canEditSignerFields, initCanvas]);

  useEffect(() => {
    setSignFieldErrors((p) => {
      if (!p.signature) return p;
      const n = { ...p };
      delete n.signature;
      return n;
    });
  }, [sigMethod]);

  const onCanvasDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDrawing(true);
    const pos = getCanvasPos(e, canvas);
    const ctx2 = canvas.getContext("2d");
    ctx2.beginPath();
    ctx2.moveTo(pos.x, pos.y);
  };

  const onCanvasMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pos = getCanvasPos(e, canvas);
    const ctx2 = canvas.getContext("2d");
    ctx2.lineTo(pos.x, pos.y);
    ctx2.stroke();
    setCanvasDirty(true);
    setSignFieldErrors((p) => {
      if (!p.signature) return p;
      const n = { ...p };
      delete n.signature;
      return n;
    });
  };

  const onCanvasUp = () => {
    setDrawing(false);
  };

  function collectSignatureData() {
    if (sigMethod === "draw") {
      const canvas = canvasRef.current;
      if (!canvas || !canvasDirty) return "";
      return canvas.toDataURL("image/png");
    }
    if (sigMethod === "type") {
      const t = String(typeText || "").trim();
      if (!t) return "";
      return textSignatureToDataUrl(t);
    }
    return String(uploadDataUrl || "").trim();
  }

  async function saveSignature() {
    if (!canSign || signing) return;

    const validation = validateSellerSignForm({
      fullName,
      designation,
      sigMethod,
      canvasDirty,
      canvasRef,
      typeText,
      uploadDataUrl,
    });

    if (!validation.ok) {
      setSignFieldErrors(validation.errors);
      const first =
        validation.errors.fullName ||
        validation.errors.designation ||
        validation.errors.signature ||
        "Please fix the highlighted fields.";
      toast.error(first);
      return;
    }

    setSignFieldErrors({});

    try {
      setSigning(true);
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_AGREEMENT_SIGN(rfqId), {
        fullName: validation.fullName,
        designation: validation.designation,
        signatureMethod: sigMethod,
        signatureData: validation.signatureData,
        signerRole: "manufacturer",
      });

      toast.success("Agreement signed successfully");
      const next = await loadContext();
      setCtx(next);
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.message || e?.message || "Could not sign agreement";
      const fe = mapApiFieldErrorsToSignState(data?.fieldErrors);
      if (Object.keys(fe).length) setSignFieldErrors(fe);
      toast.error(msg);
    } finally {
      setSigning(false);
    }
  }

  function onUploadFile(ev) {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) {
      setSignFieldErrors((prev) => ({ ...prev, signature: "Please choose an image file (PNG, JPG, etc.)." }));
      toast.error("Please choose an image file.");
      ev.target.value = "";
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      setSignFieldErrors((prev) => ({
        ...prev,
        signature: `Image must be ${Math.round(UPLOAD_MAX_BYTES / (1024 * 1024))} MB or smaller.`,
      }));
      toast.error("File is too large.");
      ev.target.value = "";
      return;
    }
    setSignFieldErrors((prev) => {
      const next = { ...prev };
      delete next.signature;
      return next;
    });
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error("Could not read the file.");
      setSignFieldErrors((prev) => ({ ...prev, signature: "Could not read the file." }));
    };
    reader.onload = () => {
      if (typeof reader.result === "string") setUploadDataUrl(reader.result);
    };
    reader.readAsDataURL(file);
  }

  function shareEmail() {
    const to = signView?.buyerContactEmail;
    const sub = encodeURIComponent(`Agreement ${signView?.agreementId || ""}`);
    const body = encodeURIComponent(
      `Regarding agreement ${signView?.agreementId || ""} (RFQ #${rfqId}).\n\n`,
    );
    if (to) {
      window.location.href = `mailto:${to}?subject=${sub}&body=${body}`;
    } else {
      toast.info("Buyer contact email is not on file; add it in the agreement or copy details manually.");
    }
  }

  function requestChanges() {
    shareEmail();
  }

  if (loading) {
    return (
      <div className="cm-sign-page">
        <div className="cm-sign-inner">
          <p className="text-muted">Loading agreement…</p>
        </div>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="cm-sign-page">
        <div className="cm-sign-inner">
          <div className="alert alert-warning">No purchase agreement found for this RFQ.</div>
          <Link to="/seller/cm-rfqs" className="cm-agreement-btn-secondary">
            Back to RFQs
          </Link>
        </div>
      </div>
    );
  }

  const mfrSig = getObj(form.signatures?.manufacturer);

  return (
    <div className="cm-sign-page">
      <div className="cm-sign-inner cm-sign-no-print">
        <Link to="/seller/cm-rfqs" className="cm-sign-back">
          <FaArrowLeft aria-hidden /> Back to RFQs
        </Link>

        <div className="cm-sign-hero">
          <div>
            <h1 className="cm-sign-title">Sign Agreement</h1>
            <p className="cm-sign-sub">Review and digitally sign the manufacturing agreement.</p>
          </div>
          {signView ? (
            <span className="cm-sign-badge">
              {signView.signaturesDone}/{signView.signaturesTotal} Signed
            </span>
          ) : null}
        </div>

        <div className="cm-sign-layout">
          <aside className="cm-sign-sidebar">
            <div className="cm-sign-card cm-sign-instructions">
              <h3>Before you sign</h3>
              <ol>
                <li>Review the agreement carefully.</li>
                <li>Request changes if any commercial or legal terms need adjustment.</li>
                <li>Confirm the contract value, dates, and milestones match your quotation.</li>
                <li>Sign as the authorized representative of your company.</li>
                <li>Use Draw, Type, or Upload for your signature image.</li>
                <li>Save your signature to complete the manufacturer&apos;s part.</li>
                <li>The buyer will sign separately from their account.</li>
              </ol>
            </div>
            <div className="cm-sign-card">
              <h3>Quick actions</h3>
              <div className="cm-sign-actions">
                <button type="button" className="cm-sign-action-btn" onClick={() => setPreviewOpen(true)}>
                  <FaEye aria-hidden /> Preview agreement
                </button>
                <button type="button" className="cm-sign-action-btn" onClick={requestChanges}>
                  <FaCommentDots aria-hidden /> Request changes
                </button>
                <button
                  type="button"
                  className="cm-sign-action-btn"
                  onClick={() => window.print()}
                >
                  <FaDownload aria-hidden /> Download draft
                </button>
                <button type="button" className="cm-sign-action-btn" onClick={shareEmail}>
                  <FaEnvelope aria-hidden /> Share via email
                </button>
              </div>
            </div>
          </aside>

          <div>
            <div className="cm-sign-card cm-sign-summary">
              {signView ? (
                <span className="cm-sign-summary-id">{signView.agreementId}</span>
              ) : null}
              <h2>{signView?.title || dash(form.agreementInfo.title)}</h2>
              <div className="cm-sign-summary-grid">
                <div className="cm-sign-kv">
                  <label>Contract value</label>
                  <span>{signView?.contractValue ?? "—"}</span>
                </div>
                <div className="cm-sign-kv">
                  <label>Start date</label>
                  <span>{signView?.startDate ?? "—"}</span>
                </div>
                <div className="cm-sign-kv">
                  <label>End date</label>
                  <span>{signView?.endDate ?? "—"}</span>
                </div>
                <div className="cm-sign-kv">
                  <label>Duration</label>
                  <span>
                    {signView?.durationDays != null ? `${signView.durationDays} days` : "—"}
                  </span>
                </div>
              </div>
              <div className="cm-sign-parties">
                <div className="cm-sign-party">
                  <FaUser aria-hidden />
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>Buyer</div>
                    {signView?.buyerName ?? "—"}
                  </div>
                </div>
                <div className="cm-sign-party">
                  <FaIndustry aria-hidden />
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>Manufacturer</div>
                    {signView?.manufacturerName ?? "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className="cm-sign-card cm-sign-form">
              <h3>Signature</h3>

              {isSigned ? (
                <div className="cm-sign-done">
                  This agreement is fully signed on the manufacturer side.
                  {mfrSig?.fullName ? (
                    <>
                      {" "}
                      Signed as <strong>{mfrSig.fullName}</strong>
                      {mfrSig.designation ? ` (${mfrSig.designation})` : ""}.
                    </>
                  ) : null}
                  {typeof mfrSig.signatureData === "string" && mfrSig.signatureData.startsWith("data:") ? (
                    <div style={{ marginTop: 12 }}>
                      <img
                        src={mfrSig.signatureData}
                        alt="Signature"
                        style={{ maxHeight: 80, border: "1px solid #e5e7eb", borderRadius: 8 }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : isCancelled ? (
                <div className="alert alert-warning mb-0">This agreement has been cancelled.</div>
              ) : awaitingBuyerSubmit ? (
                <div className="alert alert-info mb-0">
                  This agreement is still in draft or has not been submitted for signature yet.
                </div>
              ) : (
                <>
                  <div className="cm-sign-toggle-row">
                    <button
                      type="button"
                      className={`cm-sign-toggle ${signerTab === "buyer" ? "cm-sign-toggle--on" : ""}`}
                      disabled
                      title="The buyer signs from their dashboard"
                    >
                      <span className="cm-sign-toggle-label">Buyer</span>
                      <span className="cm-sign-toggle-name">{signView?.buyerName || "Buyer"}</span>
                    </button>
                    <button
                      type="button"
                      className={`cm-sign-toggle ${signerTab === "manufacturer" ? "cm-sign-toggle--on" : ""}`}
                      onClick={() => setSignerTab("manufacturer")}
                    >
                      <span className="cm-sign-toggle-label">Manufacturer</span>
                      <span className="cm-sign-toggle-name">
                        {signView?.manufacturerName || "Manufacturer"}
                      </span>
                    </button>
                  </div>

                  <div className="cm-sign-fields">
                    <div>
                      <label htmlFor="cm-sign-fullname">Full name *</label>
                      <input
                        id="cm-sign-fullname"
                        className={signFieldErrors.fullName ? "is-invalid" : undefined}
                        autoComplete="name"
                        placeholder="Enter your full name"
                        value={fullName}
                        onChange={(e) => {
                          setFullName(e.target.value);
                          setSignFieldErrors((p) => {
                            if (!p.fullName) return p;
                            const n = { ...p };
                            delete n.fullName;
                            return n;
                          });
                        }}
                        disabled={!canEditSignerFields}
                        aria-invalid={signFieldErrors.fullName ? "true" : undefined}
                        aria-describedby={signFieldErrors.fullName ? "cm-sign-fullname-err" : undefined}
                      />
                      {signFieldErrors.fullName ? (
                        <div id="cm-sign-fullname-err" className="text-danger small mt-1" role="alert">
                          {signFieldErrors.fullName}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label htmlFor="cm-sign-role">Designation *</label>
                      <input
                        id="cm-sign-role"
                        className={signFieldErrors.designation ? "is-invalid" : undefined}
                        placeholder="e.g., CEO, Director, Manager"
                        value={designation}
                        onChange={(e) => {
                          setDesignation(e.target.value);
                          setSignFieldErrors((p) => {
                            if (!p.designation) return p;
                            const n = { ...p };
                            delete n.designation;
                            return n;
                          });
                        }}
                        disabled={!canEditSignerFields}
                        aria-invalid={signFieldErrors.designation ? "true" : undefined}
                        aria-describedby={signFieldErrors.designation ? "cm-sign-role-err" : undefined}
                      />
                      {signFieldErrors.designation ? (
                        <div id="cm-sign-role-err" className="text-danger small mt-1" role="alert">
                          {signFieldErrors.designation}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="cm-sign-method-tabs">
                    {["draw", "type", "upload"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={sigMethod === m ? "cm-sign-method--active" : ""}
                        onClick={() => setSigMethod(m)}
                        disabled={!canEditSignerFields}
                      >
                        {m === "draw" ? "Draw" : m === "type" ? "Type" : "Upload"}
                      </button>
                    ))}
                  </div>

                  {signFieldErrors.signature ? (
                    <div className="alert alert-danger py-2 small mb-2" role="alert" id="cm-sign-signature-err">
                      {signFieldErrors.signature}
                    </div>
                  ) : null}

                  {sigMethod === "draw" && (
                    <>
                      <div className="cm-sign-canvas-box">
                        {!canvasDirty ? (
                          <span className="cm-sign-canvas-hint">Draw your signature below</span>
                        ) : null}
                        <canvas
                          ref={canvasRef}
                          onMouseDown={onCanvasDown}
                          onMouseMove={onCanvasMove}
                          onMouseUp={onCanvasUp}
                          onMouseLeave={onCanvasUp}
                          onTouchStart={onCanvasDown}
                          onTouchMove={onCanvasMove}
                          onTouchEnd={onCanvasUp}
                        />
                      </div>
                      <div className="cm-sign-canvas-toolbar">
                        <button
                          type="button"
                          className="cm-sign-clear"
                          onClick={() => initCanvas()}
                          disabled={!canEditSignerFields}
                        >
                          <FaRedo aria-hidden /> Clear
                        </button>
                      </div>
                    </>
                  )}

                  {sigMethod === "type" && (
                    <input
                      className={`cm-sign-type-input${signFieldErrors.signature ? " is-invalid" : ""}`}
                      placeholder="Type your name"
                      value={typeText}
                      onChange={(e) => {
                        setTypeText(e.target.value);
                        setSignFieldErrors((p) => {
                          if (!p.signature) return p;
                          const n = { ...p };
                          delete n.signature;
                          return n;
                        });
                      }}
                      disabled={!canEditSignerFields}
                      aria-invalid={signFieldErrors.signature ? "true" : undefined}
                    />
                  )}

                  {sigMethod === "upload" && (
                    <div className="cm-sign-upload">
                      <div>Upload an image of your signature (PNG or JPG).</div>
                      <input type="file" accept="image/*" onChange={onUploadFile} disabled={!canEditSignerFields} />
                      {uploadDataUrl ? (
                        <div style={{ marginTop: 12 }}>
                          <img
                            src={uploadDataUrl}
                            alt="Upload preview"
                            style={{ maxHeight: 100, borderRadius: 8 }}
                          />
                        </div>
                      ) : null}
                    </div>
                  )}

                  {canEditSignerFields && !canSign ? (
                    <p className="text-muted small mb-2 mb-md-3" style={{ maxWidth: 560 }}>
                      &quot;Save signature&quot; turns on only after the buyer sends the agreement for signature
                      (status <strong>submitted</strong>). Current status: <strong>{st || "—"}</strong>. If this
                      stays draft, ask the buyer to click &quot;Send for signature&quot; on the agreement page.
                    </p>
                  ) : null}
                  <div className="cm-sign-footer-actions">
                    <button
                      type="button"
                      className="cm-sign-btn-discard"
                      onClick={() => navigate("/seller/cm-rfqs")}
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      className="cm-sign-btn-save"
                      onClick={saveSignature}
                      disabled={!canSign || signing}
                    >
                      {signing ? "Saving…" : "Save signature"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Printable full document (print / download draft) */}
      <div className="d-none d-print-block">
        <AgreementPreviewBody
          form={form}
          summary={summary}
          currencySelectOptions={currencySelectOptions}
          paymentTermsSelectOptions={paymentTermsSelectOptions}
          incotermsSelectOptions={incotermsSelectOptions}
        />
      </div>

      {previewOpen ? (
        <div
          className="cm-sign-modal-overlay cm-sign-no-print"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cm-sign-preview-title"
        >
          <div className="cm-sign-modal-panel">
            <div className="cm-sign-modal-head">
              <h2 id="cm-sign-preview-title" style={{ margin: 0, fontSize: 18 }}>
                Agreement preview
              </h2>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="cm-agreement-btn-secondary" onClick={() => window.print()}>
                  <FaPrint aria-hidden /> Print
                </button>
                <button
                  type="button"
                  className="cm-sign-modal-close"
                  onClick={() => setPreviewOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <AgreementPreviewBody
              form={form}
              summary={summary}
              currencySelectOptions={currencySelectOptions}
              paymentTermsSelectOptions={paymentTermsSelectOptions}
              incotermsSelectOptions={incotermsSelectOptions}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
